import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../core/providers/AuthProvider';
import { usePayments } from '../hooks/usePayments';
import { useStudents } from '../../Students/hooks/useStudents';
import { ROLES } from '../../../shared/constant/roles';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { SCHOOL_FEES, isDiscount, isWaived } from '../../../shared/constant/schoolFees';
import { compareByGradeOrder } from '../../../shared/utils/sortByGrade';
import { confirmAction, showAlert } from '../../../shared/utils/dialogs';
import Pagination from '../../../shared/components/Pagination';
import PaymentFormModal from './PaymentFormModal';

const peso = (value) => `${Number(value) < 0 ? '-' : ''}₱${Math.abs(Number(value)).toLocaleString()}`;
const PAGE_SIZE = 10;

function PaymentsPage() {
  const { role } = useAuth();
  const canDelete = role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN;
  const { payments, loading, error, create, bulkCreate, update, remove } = usePayments();
  const { students } = useStudents();
  const [modalState, setModalState] = useState(null); // null | 'new' | payment object
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [schoolYearFilter, setSchoolYearFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [page, setPage] = useState(1);

  const categoryOptions = useMemo(
    () => [...new Set(payments.map((p) => p.category).filter(Boolean))].sort(),
    [payments]
  );

  const sectionOptions = useMemo(() => {
    const source = gradeFilter ? students.filter((s) => s.grade_level === gradeFilter) : students;
    return [...new Set(source.map((s) => s.section).filter(Boolean))].sort(compareByGradeOrder);
  }, [students, gradeFilter]);

  useEffect(() => {
    if (sectionFilter && !sectionOptions.includes(sectionFilter)) {
      setSectionFilter('');
    }
  }, [sectionOptions, sectionFilter]);

  const schoolYearOptions = useMemo(
    () => [...new Set(students.map((s) => s.school_year).filter(Boolean))].sort().reverse(),
    [students]
  );

  const studentSectionById = useMemo(() => {
    const map = new Map();
    for (const s of students) map.set(s.id, s.section);
    return map;
  }, [students]);

  const studentSchoolYearById = useMemo(() => {
    const map = new Map();
    for (const s of students) map.set(s.id, s.school_year);
    return map;
  }, [students]);

  const getSection = (p) => (p.student_id ? studentSectionById.get(p.student_id) : undefined);
  const getSchoolYear = (p) => p.school_year ?? (p.student_id ? studentSchoolYearById.get(p.student_id) : undefined);

  // A student is Full once every fee category has a line. A waived PTA line (₱0) still counts,
  // so a discounted student who paid ₱380 is Full.
  const paidCategoriesByStudent = useMemo(() => {
    const byStudent = new Map();
    for (const p of payments) {
      const key = p.student_id ?? p.payer_name;
      if (!byStudent.has(key)) byStudent.set(key, new Set());
      byStudent.get(key).add(p.category);
    }
    return byStudent;
  }, [payments]);

  const getStatus = (p) => {
    const paid = paidCategoriesByStudent.get(p.student_id ?? p.payer_name);
    return paid && SCHOOL_FEES.every((fee) => paid.has(fee.category)) ? 'Full' : 'Partial';
  };

  const categoryLabel = (p) => (isWaived(p) ? 'PTA (waived)' : p.category || '—');

  const filteredPayments = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return payments.filter((p) => {
      if (gradeFilter && p.grade_level !== gradeFilter) return false;
      if (sectionFilter && getSection(p) !== sectionFilter) return false;
      if (schoolYearFilter && getSchoolYear(p) !== schoolYearFilter) return false;
      if (categoryFilter && p.category !== categoryFilter) return false;
      if (dateFilter && p.payment_date !== dateFilter) return false;
      if (statusFilter && getStatus(p) !== statusFilter) return false;
      if (!q) return true;
      return [p.payer_name, p.category, p.or_number, p.grade_level, p.remarks]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payments,
    searchTerm,
    gradeFilter,
    sectionFilter,
    schoolYearFilter,
    categoryFilter,
    dateFilter,
    statusFilter,
    paidCategoriesByStudent,
    studentSectionById,
    studentSchoolYearById,
  ]);

  const totalCollected = useMemo(
    () => filteredPayments.reduce((sum, p) => sum + Number(p.amount), 0),
    [filteredPayments]
  );

  const groupedPayments = useMemo(() => {
    const map = new Map();
    const order = [];
    for (const p of filteredPayments) {
      const key = p.batch_id ?? p.id;
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key).push(p);
    }
    return order.map((key) => ({ key, rows: map.get(key) }));
  }, [filteredPayments]);

  const totalPages = Math.max(1, Math.ceil(groupedPayments.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedGroups = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return groupedPayments.slice(start, start + PAGE_SIZE);
  }, [groupedPayments, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, gradeFilter, sectionFilter, schoolYearFilter, categoryFilter, dateFilter, statusFilter]);

  const hasActiveFilters = Boolean(
    searchTerm || gradeFilter || sectionFilter || schoolYearFilter || categoryFilter || dateFilter || statusFilter
  );

  const clearFilters = () => {
    setSearchTerm('');
    setGradeFilter('');
    setSectionFilter('');
    setSchoolYearFilter('');
    setCategoryFilter('');
    setDateFilter('');
    setStatusFilter('');
  };

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDelete = async (payment) => {
    const confirmed = await confirmAction({
      title: 'Delete payment?',
      text: `Delete payment record for ${payment.payer_name}? This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!confirmed) return;
    try {
      await remove(payment.id, payment);
    } catch (err) {
      showAlert({ title: 'Failed to delete payment', text: err.message || 'Failed to delete payment' });
    }
  };

  const handleSubmit = (rows) => {
    if (modalState && modalState !== 'new') {
      return update(modalState.id, rows[0], modalState);
    }
    if (rows.length === 1) {
      return create(rows[0]);
    }
    return bulkCreate(rows);
  };

  const renderRowActions = (payment) => (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => setModalState(payment)}
        className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
        aria-label="Edit payment"
      >
        <PencilIcon className="h-4 w-4" />
      </button>
      {canDelete && (
        <button
          type="button"
          onClick={() => handleDelete(payment)}
          className="p-1.5 rounded-md text-danger hover:bg-danger/10"
          aria-label="Delete payment"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  return (
    <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 sm:px-5 py-4 bg-navy-light space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Payments</h2>
            <p className="text-sm text-gray-200">Recorded SPTA contributions</p>
          </div>
          <button
            type="button"
            onClick={() => setModalState('new')}
            className="flex items-center justify-center gap-1.5 rounded-md bg-gold text-navy text-lg font-medium px-3 py-2 hover:bg-gold-dark"
          >
            <PlusIcon className="h-4 w-4" />
            Record Payment
          </button>
        </div>

        <div className="flex items-center gap-2 w-full">
          <div className="relative flex-1 min-w-0">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by payer, category, OR #…"
              className="w-full rounded-md border-0 pl-9 pr-3 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            aria-label="Toggle filters"
            aria-pressed={showFilters}
            className={`shrink-0 p-2.5 rounded-md border transition ${
              showFilters
                ? 'bg-white text-navy border-white'
                : 'border-gray-300 text-gray-200 hover:text-white hover:border-white'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
          </button>
        </div>

        {showFilters && (
        <div className="flex flex-wrap items-center gap-2 w-full">
          <select
            value={gradeFilter}
            onChange={(e) => setGradeFilter(e.target.value)}
            className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
          >
            <option value="">All Grades</option>
            {GRADE_LEVELS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>

          <select
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
          >
            <option value="">All Sections</option>
            {sectionOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <select
            value={schoolYearFilter}
            onChange={(e) => setSchoolYearFilter(e.target.value)}
            className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
          >
            <option value="">All School Years</option>
            {schoolYearOptions.map((sy) => (
              <option key={sy} value={sy}>
                {sy}
              </option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
          >
            <option value="">All Categories</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
          >
            <option value="">All Statuses</option>
            <option value="Full">Full</option>
            <option value="Partial">Partial</option>
          </select>

          <button
            type="button"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="w-full sm:w-auto text-lg text-gray-200 border border-gray-300 px-3 py-2 rounded-md hover:text-white whitespace-nowrap disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed disabled:hover:text-gray-200"
          >
            Clear filters
          </button>
        </div>
        )}
      </div>

      {!loading && !error && groupedPayments.length > 0 && (
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          totalItems={groupedPayments.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      {loading ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">Loading payments…</p>
      ) : error ? (
        <p className="px-5 py-8 text-sm text-danger text-center">{error}</p>
      ) : filteredPayments.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">
          {payments.length === 0 ? 'No payments recorded' : 'No payments match your search/filters'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200 text-md text-gray-500 uppercase">
                <th className="py-2 pl-5 pr-4 font-semibold whitespace-nowrap">Payer</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Grade</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Section</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">School Year</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Category</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Amount</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">OR #</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Date</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Status</th>
                <th className="py-2 pr-5 font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedGroups.map(({ key, rows }) => {
                if (rows.length === 1) {
                  const payment = rows[0];
                  return (
                    <tr key={payment.id} className="hover:bg-gold/10">
                      <td className="py-3 pl-5 pr-4 font-medium text-gray-900 whitespace-nowrap">
                        {payment.payer_name}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{payment.grade_level}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{getSection(payment) || '—'}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{getSchoolYear(payment) || '—'}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{categoryLabel(payment)}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{peso(payment.amount)}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{payment.or_number || '—'}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{payment.payment_date}</td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            getStatus(payment) === 'Full' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning-dark'
                          }`}
                        >
                          {getStatus(payment)}
                        </span>
                      </td>
                      <td className="py-3 pr-5 whitespace-nowrap">{renderRowActions(payment)}</td>
                    </tr>
                  );
                }

                const isExpanded = expandedGroups.has(key);
                const first = rows[0];
                const groupTotal = rows.reduce((sum, r) => sum + Number(r.amount), 0);

                return (
                  <Fragment key={key}>
                    <tr
                      onClick={() => toggleGroup(key)}
                      className="bg-gray-50 hover:bg-gold/10 cursor-pointer font-medium"
                    >
                      <td className="py-3 pl-5 pr-4 text-gray-900 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                          )}
                          {first.payer_name}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{first.grade_level}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{getSection(first) || '—'}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{getSchoolYear(first) || '—'}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">
                        {rows.filter((r) => !isDiscount(r)).length} categories
                        {rows.some(isWaived) ? ' (PTA waived)' : rows.some(isDiscount) ? ' + discount' : ''}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{peso(groupTotal)}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{first.or_number || '—'}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{first.payment_date}</td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            getStatus(first) === 'Full' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning-dark'
                          }`}
                        >
                          {getStatus(first)}
                        </span>
                      </td>
                      <td className="py-3 pr-5 whitespace-nowrap text-xs text-gray-400">
                        {isExpanded ? 'Collapse' : 'Expand'}
                      </td>
                    </tr>
                    {isExpanded &&
                      rows.map((payment) => (
                        <tr key={payment.id} className="bg-gray-50/50 hover:bg-gold/10">
                          <td className="py-2 pl-10 pr-4 text-gray-500 whitespace-nowrap">{payment.payer_name}</td>
                          <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{payment.grade_level}</td>
                          <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{getSection(payment) || '—'}</td>
                          <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{getSchoolYear(payment) || '—'}</td>
                          <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">{categoryLabel(payment)}</td>
                          <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">{peso(payment.amount)}</td>
                          <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{payment.or_number || '—'}</td>
                          <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{payment.payment_date}</td>
                          <td className="py-2 pr-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                getStatus(payment) === 'Full'
                                  ? 'bg-success/10 text-success'
                                  : 'bg-warning/10 text-warning-dark'
                              }`}
                            >
                              {getStatus(payment)}
                            </span>
                          </td>
                          <td className="py-2 pr-5 whitespace-nowrap">{renderRowActions(payment)}</td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && filteredPayments.length > 0 && (
        <div className="flex justify-between items-center mx-3 sm:mx-5 mb-4 px-4 py-3 rounded-lg text-xl sm:text-2xl font-bold bg-success/10 text-success">
          <span>Total Collected</span>
          <span>{peso(totalCollected)}</span>
        </div>
      )}

      {modalState && (
        <PaymentFormModal
          payment={modalState === 'new' ? null : modalState}
          onClose={() => setModalState(null)}
          onSubmit={handleSubmit}
        />
      )}
    </section>
  );
}

export default PaymentsPage;
